/**
 * PersonaGetStarted Component
 *
 * Empty-state card shown on persona surfaces when the user has no data.
 * Displays the persona icon, a "Get started with [persona]" heading,
 * three numbered steps (Import, Analyze, View), and a CTA button.
 *
 * @module components/onboarding/PersonaGetStarted
 */

import React from 'react';
import PropTypes from 'prop-types';
import { ArrowRight, Upload, Zap, BarChart3 } from 'lucide-react';
import Button from '../ui/Button';

const STEPS = [
  {
    icon: Upload,
    title: 'Import',
    description: 'Connect GitHub and import your repositories',
    color: 'text-secondary-400',
    bg: 'bg-secondary-500/10',
  },
  {
    icon: Zap,
    title: 'Analyze',
    description: 'Our AI examines code patterns and skills',
    color: 'text-warning-400',
    bg: 'bg-warning-500/10',
  },
  {
    icon: BarChart3,
    title: 'View',
    description: 'Explore insights tailored to your workflow',
    color: 'text-success-400',
    bg: 'bg-success-500/10',
  },
];

const PersonaGetStarted = ({ persona, onGetStarted }) => {
  if (!persona) return null;

  const Icon = persona.icon;

  return (
    <div className="bg-black/20 backdrop-blur-sm border border-white/10 rounded-2xl p-8 shadow-glass text-center max-w-lg mx-auto">
      {/* Persona icon */}
      <div className="inline-flex p-4 rounded-full bg-primary-500/15 mb-5">
        <Icon className="w-10 h-10 text-primary-400" />
      </div>

      {/* Heading */}
      <h2 className="text-xl font-bold text-white mb-2">
        Get started with {persona.title}
      </h2>
      <p className="text-gray-400 text-sm mb-8 max-w-md mx-auto">
        {persona.description}
      </p>

      {/* 3-step flow */}
      <div className="flex flex-col sm:flex-row items-stretch gap-4 mb-8">
        {STEPS.map((step, i) => {
          const StepIcon = step.icon;
          return (
            <div
              key={step.title}
              className="flex-1 p-4 bg-white/[0.04] rounded-xl border border-white/10 text-left relative"
            >
              {/* Step number */}
              <span className="absolute -top-2.5 -left-2.5 w-6 h-6 rounded-full bg-gray-800 border border-white/20 flex items-center justify-center text-xs font-bold text-gray-400">
                {i + 1}
              </span>
              <div className={`inline-flex p-2 rounded-lg ${step.bg} mb-3`}>
                <StepIcon className={`w-5 h-5 ${step.color}`} />
              </div>
              <div className="text-sm font-medium text-white mb-1">{step.title}</div>
              <div className="text-xs text-gray-500">{step.description}</div>
            </div>
          );
        })}
      </div>

      {/* CTA */}
      <Button
        variant="gradient"
        size="lg"
        onClick={onGetStarted}
        icon={ArrowRight}
        iconPosition="right"
      >
        Start Importing
      </Button>
    </div>
  );
};

PersonaGetStarted.propTypes = {
  /** Persona configuration object (from PERSONA_CONFIGS) */
  persona: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
  }).isRequired,
  /** Called when the user clicks the CTA to begin the import flow */
  onGetStarted: PropTypes.func.isRequired,
};

export default PersonaGetStarted;
